import { useEffect, useRef, useState } from 'react';
import { initOnRamp } from '@coinbase/cbpay-js';
import { toast } from 'sonner';
import { delay } from '@/hooks/LitSessionContext/useLitAuth';
import { useRouter } from 'next/router';
import { useSession } from '@/contexts/LitSessionProvider';
import { useMixpanel } from '@/lib/misc/mixpanel';
type InitOnRampOptions = Parameters<typeof initOnRamp>[0];
type CoinbaseButtonProps = {
  destinationWalletAddressEVM: string;
  destinationWalletAddressSolana: string;
};
export default function useCoinbaseButton({
  destinationWalletAddressEVM,
  destinationWalletAddressSolana
}: CoinbaseButtonProps) {
  const [isReady, setIsReady] = useState(false);
  const onrampInstance = useRef();
  const router = useRouter();
  const {
    PKP
  } = useSession();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  useEffect(() => {
    const options: InitOnRampOptions = {
      appId: 'c799aa84-00ef-4e64-8b4f-f75f85ba88cb',
      target: '#cbpay-container',
      widgetParameters: {
        destinationWallets: [{
          address: destinationWalletAddressEVM,
          blockchains: ['optimism', 'arbitrum', 'base', 'ethereum', 'avalanche-c-chain', 'polygon']
        }, {
          address: destinationWalletAddressSolana,
          blockchains: ['solana']
        }]
      },
      onSuccess: async () => {
        LogToMixpanel({
          pkp: PKP,
          eventName: EVENTS.deposit.finalizeDeposit,
          category: CATEGORIES.deposit,
          metaData: {
            type: 'coinbase'
          }
        });
        toast('All Done!', {
          position: 'bottom-right',
          description: 'Heading back to your dashboard.'
        });
        await delay(2000);
        router.push('/home');
      },
      onExit: async () => {
        LogToMixpanel({
          pkp: PKP,
          eventName: EVENTS.deposit.finalizeDeposit,
          category: CATEGORIES.deposit,
          metaData: {
            type: 'coinbase',
            note: 'Potential Failure'
          }
        });
        router.push('/home');
      },
      onEvent: event => {
        console.log('coinbase event', event);
      },
      experienceLoggedIn: 'embedded',
      experienceLoggedOut: 'popup'
    };
    if (onrampInstance.current) {
      onrampInstance.current.destroy();
    }
    initOnRamp(options, (error, instance) => {
      if (instance) {
        onrampInstance.current = instance;
        setIsReady(true);
      }
    });
  }, [destinationWalletAddressEVM, destinationWalletAddressSolana]);
  const generateCoinbasePayUrl = config => {
    if (!config?.options?.appId) {
      throw new Error('Invalid configuration: appId is required');
    }
    const baseUrl = 'https://pay.coinbase.com/landing';

    // Extract the required parameters
    const params = {
      appId: config.options.appId,
      widget: config.options.widget || 'buy',
      destinationWallets: config.options.appParams?.destinationWallets || []
    };

    // Construct URL with parameters
    const queryString = new URLSearchParams({
      appId: params.appId,
      widget: params.widget,
      destinationWallets: JSON.stringify(params.destinationWallets)
    }).toString();
    return `${baseUrl}?${queryString}`;
  };

  // Modified handleOnPress function
  const handleOnPress = () => {
    LogToMixpanel({
      pkp: PKP,
      eventName: EVENTS.deposit.startDeposit,
      category: CATEGORIES.deposit,
      metaData: {
        type: 'coinbase'
      }
    });
    if (onrampInstance.current) {
      // onrampInstance.current.open()
      const url = generateCoinbasePayUrl(onrampInstance.current);
      window.open(url, '_blank'); // or handle the URL as needed
    } else {
      console.error('Onramp instance not initialized');
    }
  };
  return {
    isReady,
    handleOnPress
  };
}